import { faPencilAlt } from "@fortawesome/free-solid-svg-icons";
import { B2Button } from "@hdb/biome-2";
import { cloneDeep } from "lodash";
import { FC, createRef, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DynamicFormComponent from "src/shared/dynamic-form/dynamic-form-builder.component";
import {
  addField,
  closeField,
  setValues,
} from "src/shared/dynamic-form/dynamic-form-util";
import FilterFormComponent, {
  FilterFormConfig,
} from "src/shared/filter-form/filter-form.component";
import { getDeviceByBlock } from "src/store/slices/dms-dashboard.slice";
import { getBlocksBySiteCode } from "src/store/slices/location-block.slice";
import { getSiteByTCCode } from "src/store/slices/location-site.slice";
import {
  addSimCard,
  editSimCardDataByID,
  getSimCardData,
  getSimCardDataByID,
  setFilter,
} from "src/store/slices/sim-card.slice";
import * as yup from "yup";
import ModalComponent from "../../../../../core/components/modal/modal.component";
import TableComponent from "../../../../../core/components/table/table.component";
import HasPermission from "../../../../../core/guards/has-permission";
import SkeletonComponent from "../../../../../shared/custom-spinner/skeleton.component";
import FormBuilderComponent, {
  FormConfig,
} from "../../../../../shared/form-builder/form-builder.component";
import isObjectNullable from "../../../../../shared/helpers/isObjectNullable";
import {
  getTownCouncils
} from "../../../../../store/slices/town-council.slice";
import "../../../admin.scss";
import SearchBarComponent from "../../search-bar/search-bar.component";

interface SimCardTableComponentProps {
  columns: any;
  data: any[];
  totalNumberOfRecords: number;
  pageSize: number;
  current: number;
  onChangePage: any;
  onChangeSort: any;
  onRefetchData: () => void;
  isLoading: boolean;
  onSearch: (val: string) => void;
  filter: any;
}

const SimCardTableComponent: FC<SimCardTableComponentProps> = ({
  columns,
  data,
  totalNumberOfRecords,
  pageSize,
  current,
  onChangePage,
  onChangeSort,
  onRefetchData,
  isLoading,
  onSearch,
  filter,
}) => {
  const formRefForFilter = createRef();
  const formRefForAddSimFilter = createRef();
  const formRefForAddSim = useRef<any>(null);
  const formRefForEditSim = createRef();
  const [isAddSimModalOpen, setIsAddSimModalOpen] = useState<boolean>(false);
  const [isEditSimModalOpen, setIsEditSimModalOpen] = useState<boolean>(false);
  const [selectedSimCardIDToEdit, setSelectedSimCardIDToEdit] =
    useState<number>(0);
  const [initialValuesForEditSim, setInitialValuesForEditSim] = useState<any>({
    deviceCode: "",
    serialNumber: "",
  });

  const initialValuesForAddSimObjects = {
    motionTime: [
      {
        id: 0,
        deviceCode: "",
        serialNumber: "",
      },
    ],
  };

  const [initialValuesForAddSim, setInitialValuesForAddSim] = useState<any>(
    initialValuesForAddSimObjects,
  );

  const [formNumber, setFormNumber] = useState<number>(1);

  const [tcOptions, setTCOptions] = useState<any>([]);
  const [siteOptions, setSiteOptions] = useState<any>([]);
  const [blockOptions, setBlockOptions] = useState<any>([]);
  const [addSimTcOptions, setAddSimTcOptions] = useState<any>([]);
  const [addSimSiteOptions, setAddSimSiteOptions] = useState<any>([]);
  const [addSimBlockOptions, setAddSimBlockOptions] = useState<any>([]);
  const [deviceCodeOptions, setDeviceCodeOptions] = useState<any>([]);
  const [currentTC, setCurrentTC] = useState({ label: "", value: "" });

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getTownCouncils({ PageNumber: 0, pageSize: 0 }));
  }, [dispatch]);

  const townCouncilData = useSelector((state: any) => {
    return state.townCouncil.townCouncils;
  });

  const isLoadingTownCouncils = useSelector((state: any) => {
    return state.townCouncil.isLoadingTownCouncils;
  });

  const siteDataByTCCode = useSelector((state: any) => {
    return state.locationSite.sitesByTCCode;
  });

  const isLocationSitesByTCCode = useSelector((state: any) => {
    return state.locationSite.isLocationSitesByTCCode;
  });

  const blockDataBySiteCode = useSelector((state: any) => {
    return state.locationBlock.blocksBySiteCode;
  });

  const isLoadingBlocksBySiteCode = useSelector((state: any) => {
    return state.locationBlock.isLoadingBlocksBySiteCode;
  });

  const deviceByBlock = useSelector((state: any) => {
    return state.dmsDashboard.deviceByBlock;
  });

  const isLoadingDeviceByBlock = useSelector((state: any) => {
    return state.dmsDashboard.isLoadingdeviceByBlock;
  });

  const simCardDataByID = useSelector((state: any) => {
    return state.simCard.simCardDataByID;
  });

  const isLoadingSimCardDataByID = useSelector((state: any) => {
    return state.simCard.isLoadingSimCardDataByID;
  });

  useEffect(() => {
    const tcs = townCouncilData?.data?.map((tc: any) => ({
      label: tc?.name,
      value: tc?.tcCode,
    }));

    if (!tcs) return;

    // if user is TC Admin only 1 tc is available
    if (tcs?.length === 1) {
      setCurrentTC(tcs[0]);
    }

    setAddSimTcOptions(tcs);
    setTCOptions(tcs);
  }, [townCouncilData]);

  useEffect(() => {
    if (currentTC.value.length) {
      setInitialValuesForFilter((prev) => ({
        ...prev,
        townCouncil: currentTC.value,
      }));

      dispatch(getSiteByTCCode({ tcCode: currentTC.value }));
    }
  }, [currentTC]);

  useEffect(() => {
    const sites = siteDataByTCCode?.data?.map((site: any) => ({
      label: site?.name,
      value: site?.siteCode,
    }));

    if (!sites?.length) return;

    if (isAddSimModalOpen) {
      setAddSimSiteOptions(sites);
    } else {
      setSiteOptions(sites);
    }
  }, [siteDataByTCCode]);

  useEffect(() => {
    const blocks = blockDataBySiteCode?.data?.map((block: any) => ({
      label: block?.blockName,
      value: block?.blockCode,
    }));

    if (!blocks) return;

    if (isAddSimModalOpen) {
      setAddSimBlockOptions(blocks);
    } else {
      setBlockOptions(blocks);
    }
  }, [blockDataBySiteCode]);

  useEffect(() => {
    if (!deviceByBlock?.length) return;

    const devices = deviceByBlock.map((device: any) => ({
      label: device?.deviceCode,
      value: device?.deviceCode,
    }));

    setDeviceCodeOptions(devices);
  }, [deviceByBlock]);

  useEffect(() => {
    if (!simCardDataByID || isObjectNullable(simCardDataByID)) return;
    setInitialValuesForEditSim({
      deviceCode: simCardDataByID?.deviceCode || "",
      serialNumber: simCardDataByID?.serialNumber || "",
    });
  }, [simCardDataByID]);

  useEffect(() => {
    if (isAddSimModalOpen) {
      setAddSimFormValues(initialValuesForAddSimObjects);
    }
  }, [deviceCodeOptions]);

  useEffect(() => {
    const formForFilter: any = formRefForFilter.current;
    if (formForFilter) {
      formForFilter.resetForm();
    }

    setInitialValuesForFilter({
      townCouncil: "",
      site: "",
      block: "",
    });

    dispatch(setFilter({}));
    setSiteOptions([]);
    setBlockOptions([]);

    const formForAddSimFilter: any = formRefForAddSimFilter.current;
    if (formForAddSimFilter) {
      formForAddSimFilter.resetForm();
    }

    setInitialValuesForAddSimFilter({
      townCouncil: "",
      site: "",
      block: "",
    });

    setAddSimSiteOptions([]);
    setAddSimBlockOptions([]);
  }, []);

  //
  const handleOnClickSearch = ({ PageNumber, pageSize, searchText }: any) => {
    return getSimCardData({
      PageNumber,
      pageSize,
      SortBy: "SerialNumber",
      SortDirection: "desc",
      TcCode: filter?.townCouncil,
      SiteCode: filter?.site,
      BlockCode: filter?.block,
      searchText,
    });
  };

  const handleCloseModal = () => {
    setIsAddSimModalOpen(false);
    setIsEditSimModalOpen(false);

    setInitialValuesForAddSimFilter({
      townCouncil: "",
      site: "",
      block: "",
    });

    setAddSimSiteOptions([]);
    setAddSimBlockOptions([]);

    setInitialValuesForEditSim({
      deviceCode: "",
      serialNumber: "",
    });
    setDeviceCodeOptions([]);

    // Reset form config with empty device options
    setFormConfigForAddSim({
      controls: [
        {
          fieldArray: {
            fieldArrayConfig: {
              fieldArrayName: "motionTime",
            },
            fieldArrayControls: [getAddSimFormConfigControlGroup([])],
          },
        },
      ],
    });

    // Reset form initial values
    setInitialValuesForAddSim(initialValuesForAddSimObjects);
    setFormNumber(1);
  };

  const editAction = (id: any) => {
    setIsEditSimModalOpen(true);


    dispatch(getSimCardDataByID({ id }))
      .then(() => { })
      .catch((error: any) => {
        console.log("Error in api call: ", error);
      });

    setSelectedSimCardIDToEdit(id);
  };

  const checkForDuplicatePhoneNumbers = (motionTimeEntries: any[]) => {
    if (!motionTimeEntries || motionTimeEntries.length === 0) {
      return true;
    }

    // Get all serial numbers from entries (filter out empty ones)
    const serialNumbers = motionTimeEntries
      .map((entry: any) =>
        entry.serialNumber ? String(entry.serialNumber).trim() : "",
      )
      .filter(
        (serialNumber: string) => serialNumber && serialNumber.length > 0,
      );

    // Check if any serial number appears more than once
    const serialNumberCount = new Map();

    for (const serialNumber of serialNumbers) {
      const count = serialNumberCount.get(serialNumber) || 0;
      serialNumberCount.set(serialNumber, count + 1);

      // If we find a duplicate, return false immediately
      if (count >= 1) {
        return false;
      }
    }

    return true;
  };

  const onSubmitAddSim = () => {
    const form: any = formRefForAddSim.current;

    if (form) {
      form.handleSubmit();

      if (isObjectNullable(form.values)) {
        return;
      }

      const motionTimeEntries = form.values.motionTime || [];

      // Validate all entries
      const isValid = motionTimeEntries.every((entry: any) => {
        const { deviceCode, serialNumber } = entry;

        return (
          deviceCode &&
          serialNumber &&
          serialNumber.toString().length <= 18 &&
          /^\d+$/.test(serialNumber.toString())
        );
      });

      if (!isValid) {
        return;
      }

      // Check for duplicate SIM Card numbers
      const hasNoDuplicates = checkForDuplicatePhoneNumbers(motionTimeEntries);

      if (!hasNoDuplicates) {
        console.error("Duplicate SIM Card Number found");
        return;
      }

      // create payload for API
      const payload = motionTimeEntries.map((entry: any) => ({
        id: deviceByBlock?.find(
          (device: any) =>
            String(device.deviceCode) === String(entry.deviceCode),
        )?.id,
        deviceCode: entry.deviceCode,
        serialNumber: entry.serialNumber,
      }));

      dispatch(addSimCard({ payload })).then(() => {
        handleCloseModal();
        onReset();
        onResetAddSim();
        onRefetchData();
      });
    }
  };

  const onSubmitEditSim = () => {
    const form: any = formRefForEditSim.current;

    if (form) {
      form.handleSubmit();

      if (isObjectNullable(form.values)) {
        return;
      }

      if (
        !form?.values?.deviceCode ||
        !form?.values?.serialNumber ||
        form?.values?.serialNumber.toString().length > 18 ||
        !/^\d+$/.test(form?.values?.serialNumber.toString())
      ) {
        return;
      }

      const formValues = form.values;
      const payload = { ...formValues, id: selectedSimCardIDToEdit };

      dispatch(
        editSimCardDataByID({ payload, id: selectedSimCardIDToEdit }),
      ).then(() => {
        handleCloseModal();
        onRefetchData();
      });
    }
  };

  const modalActionConfigForAddSimCard: any = [
    {
      label: "Save",
      event: onSubmitAddSim,
      variant: "primary",
    },
  ];

  const modalActionConfigForEditSimCard: any = [
    {
      label: "Save",
      event: onSubmitEditSim,
      variant: "primary",
    },
  ];

  const actionConfig: any = [
    {
      label: "Edit",
      actionProperty: "id",
      event: editAction,
      variant: "secondary",
      className: "mx-2 my-1",
      moduleName: "SIM Card",
      actionName: "Edit-SIM Card",
      icon: faPencilAlt,
    },
  ];


  const emptyConfig = {
    title: "No SIM Card Association Found",
    description: "",
  };

  const formConfigForEditSim: FormConfig = {
    controls: [
      {
        name: "deviceCode",
        label: "Device ID",
        placeholder: "Device ID",
        type: "text",
        required: true,
        readOnly: true,
        disabled: true,
      },
      {
        name: "serialNumber",
        label: "SIM Card No",
        placeholder: "Enter a SIM Card No *",
        type: "tel",
        required: false,
        readOnly: false,
        disabled: false,
        maxLength: 18,
      },
    ],
  };

  const validationSchemaForEditSim = yup.object().shape({
    deviceCode: yup.string().required("Device ID is required"),
    serialNumber: yup
      .string()
      .max(18, "SIM Card No must be at most 18 digits")
      .required("SIM Card No is required")
      .matches(/^\d+$/, "SIM Card No must contain only digits"),
  });

  const onChangeTC = (selectedTC: any) => {
    setSiteOptions([]);
    setBlockOptions([]);

    const form: any = formRefForFilter.current;

    if (form) {
      // Get all field names except 'towncouncil'
      const fieldNames = Object.keys(form.values).filter(
        (name) => name !== "towncouncil",
      );

      // Reset each field to empty, except 'towncouncil'
      fieldNames.forEach((fieldName) => {
        if (fieldName === "site" || fieldName === "block") {
          form.setFieldValue(fieldName, "", false); // false to avoid validation after setting field
        }
      });
    }

    setInitialValuesForFilter({
      ...initialValuesForFilter,
      townCouncil: selectedTC,
      site: "",
      block: "",
    });

    dispatch(getSiteByTCCode({ tcCode: selectedTC }));
  };

  const onChangeSite = (selectedSite: any) => {
    setBlockOptions([]);

    setInitialValuesForFilter({
      ...initialValuesForFilter,
      site: selectedSite,
      block: "",
    });

    dispatch(getBlocksBySiteCode({ siteCode: selectedSite }));
  };

  const onChangeBlock = (selectedBlock: any) => {
    setInitialValuesForFilter({
      ...initialValuesForFilter,
      block: selectedBlock,
    });
  };

  const [initialValuesForFilter, setInitialValuesForFilter] = useState({
    townCouncil: filter?.townCouncil || "",
    site: filter?.site || "",
    block: filter?.block || "",
  });

  const onSubmitFilter = () => {
    const form: any = formRefForFilter.current;

    const { townCouncil, site, block } = form.values;

    if (form) {
      form.handleSubmit();

      if (!townCouncil) return;

      if (townCouncil || site || block) {
        dispatch(setFilter(form.values));
      }
    }
  };

  const onReset = () => {
    const form: any = formRefForFilter.current;
    form.resetForm();

    setInitialValuesForFilter({
      townCouncil: "",
      site: "",
      block: "",
    });

    dispatch(setFilter({}));
    setSiteOptions([]);
    setBlockOptions([]);
  };

  const filterFormConfig: FilterFormConfig = {
    controls: [
      {
        controlGroup: [
          {
            name: "townCouncil",
            placeholder: "Select Town Council *",
            type: "searchable-select",
            options: tcOptions,
            onChange: onChangeTC,
            required: true,
            isLoading: isLoadingTownCouncils,
          },
        ],
      },
      {
        controlGroup: [
          {
            name: "site",
            placeholder: "Select Site",
            type: "searchable-select",
            options: siteOptions,
            onChange: onChangeSite,
            required: false,
            isLoading: isLocationSitesByTCCode,
          },
        ],
      },
      {
        controlGroup: [
          {
            name: "block",
            placeholder: "Select Block",
            type: "searchable-select",
            options: blockOptions,
            onChange: onChangeBlock,
            required: false,
            isLoading: isLoadingBlocksBySiteCode,
          },
        ],
      },
    ],
  };

  const filterActionConfig: any = {
    label: "Filter",
    event: onSubmitFilter,
    variant: "primary",
  };

  const reset: any = {
    label: "Reset",
    event: onReset,
    variant: "secondary",
  };

  const validationSchemaForFilter = yup.object().shape({
    townCouncil: yup.string().required("Town Council is required"),
  });

  const onChangeAddSimTC = (selectedTC: any) => {
    setAddSimSiteOptions([]);
    setAddSimBlockOptions([]);
    setDeviceCodeOptions([]);

    const form: any = formRefForAddSimFilter.current;

    if (form) {
      // Get all field names except 'towncouncil'
      const fieldNames = Object.keys(form.values).filter(
        (name) => name !== "towncouncil",
      );

      // Reset each field to empty, except 'towncouncil'
      fieldNames.forEach((fieldName) => {
        if (fieldName === "site" || fieldName === "block") {
          form.setFieldValue(fieldName, "", false); // false to avoid validation after setting field
        }
      });
    }

    setInitialValuesForAddSimFilter({
      ...initialValuesForAddSimFilter,
      townCouncil: selectedTC,
      site: "",
      block: "",
    });

    dispatch(getSiteByTCCode({ tcCode: selectedTC }));
  };

  const onChangeAddSimSite = (selectedSite: any) => {
    setAddSimBlockOptions([]);
    setDeviceCodeOptions([]);

    setInitialValuesForAddSimFilter({
      ...initialValuesForAddSimFilter,
      site: selectedSite,
      block: "",
    });

    dispatch(getBlocksBySiteCode({ siteCode: selectedSite }));
  };

  const onChangeAddSimBlock = (selectedBlock: any) => {
    setDeviceCodeOptions([]);
    setInitialValuesForAddSimFilter({
      ...initialValuesForAddSimFilter,
      block: selectedBlock,
    });
  };

  const [initialValuesForAddSimFilter, setInitialValuesForAddSimFilter] =
    useState({
      townCouncil: "",
      site: "",
      block: "",
    });

  const onSubmitAddSimFilter = () => {
    const form: any = formRefForAddSimFilter.current;

    const { townCouncil, site, block } = form.values;

    if (form) {
      form.handleSubmit();

      if (!townCouncil || !site || !block) return;

      dispatch(getDeviceByBlock({ blockCode: block, isForSim: true }));
    }
  };

  const onResetAddSim = () => {
    const form: any = formRefForAddSimFilter.current;
    form.resetForm();

    setInitialValuesForAddSimFilter({
      townCouncil: "",
      site: "",
      block: "",
    });

    setAddSimSiteOptions([]);
    setAddSimBlockOptions([]);
  };

  const filterFormConfigAddSim: FilterFormConfig = {
    controls: [
      {
        controlGroup: [
          {
            name: "townCouncil",
            placeholder: "Select Town Council *",
            type: "searchable-select",
            options: addSimTcOptions,
            onChange: onChangeAddSimTC,
            required: true,
            isLoading: isLoadingTownCouncils,
          },
        ],
      },
      {
        controlGroup: [
          {
            name: "site",
            placeholder: "Select Site *",
            type: "searchable-select",
            options: addSimSiteOptions,
            onChange: onChangeAddSimSite,
            required: true,
            isLoading: isLocationSitesByTCCode,
          },
        ],
      },
      {
        controlGroup: [
          {
            name: "block",
            placeholder: "Select Block *",
            type: "searchable-select",
            options: addSimBlockOptions,
            onChange: onChangeAddSimBlock,
            required: true,
            isLoading: isLoadingBlocksBySiteCode,
          },
        ],
      },
    ],
  };

  const filterActionConfigForAddSim: any = {
    label: "Filter",
    event: onSubmitAddSimFilter,
    variant: "primary",
  };

  const validationSchemaForAddSimFilter = yup.object().shape({
    townCouncil: yup.string().required("Town Council is required"),
    site: yup.string().required("Site is required"),
    block: yup.string().required("Block is required"),
  });

  const getAddSimFormConfigControlGroup = (deviceCodeOptions: any[]) => ({
    controlGroup: [
      {
        name: "deviceCode",
        label: "Device ID",
        placeholder: "Select Device ID",
        type: "searchable-select",
        options: deviceCodeOptions,
        onChange: (_: any) => updateAddSimFormValue(_),
        required: false,
        readOnly: false,
        disabled: false,
        isLoading: isLoadingDeviceByBlock,
      },
      {
        name: "serialNumber",
        label: "SIM Card No",
        placeholder: "Enter a SIM Card No *",
        type: "tel",
        required: false,
        readOnly: false,
        disabled: false,
        inputMode: "numeric",
        maxLength: 18,
      },
    ],
  });

  const formConfigForAddSimObject = {
    controls: [
      {
        fieldArray: {
          fieldArrayConfig: {
            fieldArrayName: "motionTime",
          },
          fieldArrayControls: [
            getAddSimFormConfigControlGroup(deviceCodeOptions),
          ],
        },
      },
    ],
  };

  const [formConfigForAddSim, setFormConfigForAddSim] = useState(
    formConfigForAddSimObject,
  );

  const getDeviceCodeObjectFromValue = (value: any) => {
    return deviceCodeOptions?.find(
      (option: any) => String(option.value) === String(value),
    );
  };

  const updateAddSimFormValue = (_?: any) => {
    // Use setTimeout to ensure form state is updated before processing
    setTimeout(() => {
      const formRefClone = cloneDeep(formRefForAddSim.current);
      const copiedCurrentValues = cloneDeep(formRefClone?.values);

      if (copiedCurrentValues?.motionTime?.length === 0) {
        return;
      }

      const selectedDeviceCodes = copiedCurrentValues?.motionTime
        ?.map((item: any) => item.deviceCode)
        .filter((code: any) => code !== "" && code != null);
      const nonSelectedDeviceCodeOptions = deviceCodeOptions?.filter(
        (option: any) => !selectedDeviceCodes?.includes(String(option.value)),
      );

      setInitialValuesForAddSim(copiedCurrentValues);

      const copiedFormGroupObject = cloneDeep(formConfigForAddSimObject);

      for (let obj of copiedFormGroupObject?.controls || []) {
        if (
          obj?.fieldArray?.fieldArrayConfig?.fieldArrayName === "motionTime"
        ) {
          if (copiedCurrentValues?.motionTime?.length > 0) {
            for (let j = 0; j < copiedCurrentValues?.motionTime?.length; j++) {
              const motionTimeItem = copiedCurrentValues.motionTime[j];
              const deviceCodeValue = motionTimeItem.deviceCode;

              // Skip if deviceCode is empty string
              if (deviceCodeValue === "") {
                // Use nonSelectedDeviceCodeOptions for this control group
                obj.fieldArray.fieldArrayControls[j] =
                  getAddSimFormConfigControlGroup(nonSelectedDeviceCodeOptions);
                continue;
              }

              // Get the device code object from the value
              const deviceCodeOptionObject =
                getDeviceCodeObjectFromValue(deviceCodeValue);

              // Create updated device code options by adding the current selection to non-selected options
              let updatedDeviceCodeOptions = [...nonSelectedDeviceCodeOptions];
              if (deviceCodeOptionObject) {
                updatedDeviceCodeOptions = [
                  deviceCodeOptionObject,
                  ...nonSelectedDeviceCodeOptions,
                ];
              }

              // Update the control group at this index
              obj.fieldArray.fieldArrayControls[j] =
                getAddSimFormConfigControlGroup(updatedDeviceCodeOptions);
            }
          }
        }
      }
      // Set the updated form config
      setFormConfigForAddSim(copiedFormGroupObject);
    }, 0);
  };

  const setAddSimFormValues = (details: any) => {
    setValues({
      details: details,
      formGroupObject: formConfigForAddSimObject,
      setFormGroupInitialValues: setInitialValuesForAddSim,
      setFormConfig: setFormConfigForAddSim,
    });
  };

  const onClickAdd = (fieldArrayName?: any) => {
    addField({
      fieldArrayName,
      formRef: formRefForAddSim,
      FormGroupInitialValues: initialValuesForAddSim,
      FormConfig: formConfigForAddSim,
      setFormGroupInitialValues: setInitialValuesForAddSim,
      setFormConfig: setFormConfigForAddSim,
      FormInitialValueObject: initialValuesForAddSimObjects.motionTime[0],
      FormConfigObject: formConfigForAddSimObject.controls,
    });

    // Wait for closeField state updates to complete before updating form
    setTimeout(() => {
      updateAddSimFormValue();
      setFormNumber((prev) => prev + 1);
    }, 0);
  };

  const onClickClose = (deletionIdx: any, fieldArrayName: any) => {
    closeField({
      deletionIdx,
      fieldArrayName,
      formRef: formRefForAddSim,
      FormGroupInitialValues: initialValuesForAddSim,
      FormConfig: formConfigForAddSim,
      setFormGroupInitialValues: setInitialValuesForAddSim,
      setFormConfig: setFormConfigForAddSim,
    });

    // Wait for closeField state updates to complete before updating form
    setTimeout(() => {
      updateAddSimFormValue();
      setFormNumber((prev) => prev - 1);
    }, 0);
  };

  const validationSchemaForAddSim = yup.object().shape({
    motionTime: yup.array().of(
      yup.object().shape({
        deviceCode: yup.string().required("Device ID is required"),
        serialNumber: yup
          .string()
          .max(18, "SIM Card No must be at most 18 digits")
          .required("SIM Card No is required")
          .matches(/^\d+$/, "SIM Card No must contain only digits")
          .test(
            "no-duplicate-phone",
            "This SIM Card No is already used",
            function (value, context) {
              if (!value) return true; // Skip validation for empty values

              // Get the current index from the path
              const currentIndex = /\[(\d+)\]/.exec(context.path)?.[1];
              // console.log("currentIndex", currentIndex);

              // Get all motionTime entries from the root
              const motionTimeEntries = context.from?.[1]?.value?.motionTime || [];

              // Check if current phone number exists at a different index
              const duplicateIndex = motionTimeEntries.findIndex(
                (entry: any, index: number) =>
                  String(entry.serialNumber)?.trim() ===
                  String(value)?.trim() && index !== Number(currentIndex),
              );

              return duplicateIndex === -1; // Return true if no duplicate found
            },
          ),
      }),
    ),
  });

  return (
    <div>
      <div>
        <div className="d-flex gap-3 justify-content-end table-generic-action table-toolbar">
          <SearchBarComponent
            placeholder="Search By Serial No / Device ID"
            methodToDispatch={handleOnClickSearch}
            getSearch={onSearch}
          />
          <HasPermission
            moduleName="SIM Card"
            actionName="Add-SIM Card"
            isRoute={false}
          >
            <B2Button
              className="icon-text-btn"
              variant="primary"
              onClick={() => {
                setIsAddSimModalOpen(true);
              }}
            >
              {/* <FontAwesomeIcon icon={faCirclePlus} className="fs-7 me-2" /> */}
              Associate SIM Card
            </B2Button>
          </HasPermission>
        </div>
        <div className="my-3">
          <FilterFormComponent
            formRef={formRefForFilter}
            initialValues={initialValuesForFilter}
            formConfig={filterFormConfig}
            actionConfig={filterActionConfig}
            resetButton={reset}
            validation={validationSchemaForFilter}
          />
        </div>
        <div className="my-3">
          <TableComponent
            tableHeaders={columns}
            emptyConfig={emptyConfig}
            data={data}
            totalNumberOfRecords={totalNumberOfRecords}
            pageSize={pageSize}
            current={current}
            onChangePage={onChangePage}
            actionConfig={actionConfig}
            onChangeSort={onChangeSort}
            isLoadingData={isLoading}
          />
        </div>
      </div>

      {/* modals */}

      {isAddSimModalOpen && (
        <ModalComponent
          isConformationModal={false}
          showModal={isAddSimModalOpen}
          handleClose={handleCloseModal}
          headerTitle={"Associate SIM Card"}
          actionConfig={modalActionConfigForAddSimCard}
        >
          <FilterFormComponent
            formRef={formRefForAddSimFilter}
            initialValues={initialValuesForAddSimFilter}
            formConfig={filterFormConfigAddSim}
            actionConfig={filterActionConfigForAddSim}
            validation={validationSchemaForAddSimFilter}
          />
          <SkeletonComponent
            wrapperHeight="280px"
            skeletonHeight="50px"
            isLoading={isLoadingDeviceByBlock}
            count={3}
          >
            <DynamicFormComponent
              formRef={formRefForAddSim}
              initialValues={initialValuesForAddSim}
              formConfig={formConfigForAddSim}
              isSecurityMonitoring={true}
              onClickAddBtn={{ index: 0, func: onClickAdd }}
              onClickCloseBtn={{ index: 0, func: onClickClose }}
              validation={validationSchemaForAddSim}
              formStyle={{ backgroundColor: "white" }}
              addButtonTitle="Add"
              onClickCloseFromStart={formNumber > 1}
            />
          </SkeletonComponent>
        </ModalComponent>
      )}
      {isEditSimModalOpen && (
        <ModalComponent
          isConformationModal={false}
          showModal={isEditSimModalOpen}
          handleClose={handleCloseModal}
          headerTitle={"Edit Associated SIM Card"}
          actionConfig={modalActionConfigForEditSimCard}
        >
          <SkeletonComponent
            wrapperHeight="280px"
            skeletonHeight="50px"
            isLoading={isLoadingSimCardDataByID}
            count={3}
          >
            <FormBuilderComponent
              formRef={formRefForEditSim}
              initialValues={initialValuesForEditSim}
              validation={validationSchemaForEditSim}
              formConfig={formConfigForEditSim}
            />
          </SkeletonComponent>
        </ModalComponent>
      )}
    </div>
  );
};

export default SimCardTableComponent;
